import { arrayUnion, doc, serverTimestamp, setDoc } from 'firebase/firestore';

import { db } from '../Firebase';

const STORAGE_KEY_PREFIX = 'sf_activity_day';

/**
 * Returns the current UTC calendar day as `YYYY-MM-DD`.
 *
 * UTC is used consistently on both the client and the internal-CRM sync so the
 * "active days in the last N days" counts compare like-for-like.
 */
function getTodayKey(): string {
  return new Date().toISOString().slice(0, 10);
}

/**
 * Records that the authenticated user was active in the dashboard today.
 *
 * Writes at most once per user per day per device (guarded via localStorage),
 * accumulating a rolling set of active dates plus the latest activity time in
 * `userActivity/{uid}`. The internal-CRM sync reads this doc to compute
 * dashboard engagement metrics (last active, active days) into Attio.
 *
 * Fire-and-forget: failures are logged but never surfaced to the user.
 */
export async function recordDashboardActivity(uid: string): Promise<void> {
  const today = getTodayKey();
  const storageKey = `${STORAGE_KEY_PREFIX}:${uid}`;

  try {
    if (localStorage.getItem(storageKey) === today) {
      return;
    }
  } catch {
    // localStorage unavailable (e.g. private mode) - fall through and write.
  }

  try {
    await setDoc(
      doc(db, 'userActivity', uid),
      {
        lastActiveAt: serverTimestamp(),
        activeDates: arrayUnion(today),
      },
      { merge: true },
    );

    try {
      localStorage.setItem(storageKey, today);
    } catch {
      // Ignore localStorage write failures - worst case we write again today.
    }
  } catch (error) {
    console.error('[ACTIVITY] Failed to record dashboard activity', error);
  }
}
